import type { TriageLevel } from '@his/shared';

export interface TriageEntry {
  level: TriageLevel;
  rank: number;
  label: string;
  className: string;
}

export const TRIAGE: TriageEntry[] = [
  { level: 'level_1', rank: 1, label: 'Resuscitation', className: 'bg-destructive/15 text-destructive border-destructive/40' },
  { level: 'level_2', rank: 2, label: 'Emergent', className: 'bg-orange-500/15 text-orange-600 border-orange-500/40' },
  { level: 'level_3', rank: 3, label: 'Urgent', className: 'bg-warning/15 text-warning border-warning/40' },
  { level: 'level_4', rank: 4, label: 'Less urgent', className: 'bg-success/15 text-success border-success/40' },
  { level: 'level_5', rank: 5, label: 'Non-urgent', className: 'bg-muted text-muted-foreground border-border' },
];

export const DEFAULT_TRIAGE_LEVEL: TriageLevel = 'level_3';

export const UNIDENTIFIED_DEATH_NOTICE =
  'This arrival has not been identified. The death is recorded; identify the patient later and the certificate can be issued then.';

export function triageEntry(level: string | null | undefined): TriageEntry | undefined {
  return TRIAGE.find((entry) => entry.level === level);
}

export const MIN_MLC_REASON = 3;
export const MAX_MLC_REASON = 300;
export const MIN_IDENTITY_BASIS = 3;

export interface ErVitals {
  patientId: string;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  pulse: number | null;
  spo2: number | null;
  temperatureC: number | null;
  respiratoryRate: number | null;
  isAbnormal: boolean;
  recordedAt: string;
}

export interface ErDoctor {
  id: string;
  name: string;
  designation: string | null;
}

export interface ErPatientSummary {
  id: string;
  mrn: string;
  fullName: string;
  sex: string;
  dob: string | null;
  approximateAgeYears: number | null;
}

export interface ErBoardRow {
  id: string;
  number: string;
  patient: ErPatientSummary | null;
  unknownPatientLabel: string | null;
  arrivalMode: string;
  chiefComplaint: string | null;
  triageLevel: string | null;
  bayLabel: string | null;
  isMlc: boolean;
  arrivedAt: string;
  triagedAt: string | null;
  seenByDoctorAt: string | null;
  attendingDoctor: ErDoctor | null;
  lastVitals: ErVitals | null;
  waitingMinutes: number;
  needsTriage: boolean;
  needsDoctor: boolean;
}

export interface ErBoardResponse {
  data: ErBoardRow[];
  nextCursor: string | null;
}

export interface ErVitalRow {
  id: string;
  recordedAt: string;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  pulse: number | null;
  respiratoryRate: number | null;
  temperatureC: number | null;
  spo2: number | null;
  isAbnormal: boolean;
}

export interface ErOrderRow {
  id: string;
  orderedAt: string;
  type: string;
  status: string;
  items: { id: string; label: string }[];
}

export interface ErCaseDetail {
  id: string;
  number: string;
  branchId: string;
  patientId: string | null;
  unknownPatientLabel: string | null;
  arrivalMode: string;
  chiefComplaint: string | null;
  triageLevel: string | null;
  bayLabel: string | null;
  triageNotes: string | null;
  isMlc: boolean;
  mlcDetails: string | null;
  broughtByName: string | null;
  broughtByPhone: string | null;
  arrivedAt: string;
  triagedAt: string | null;
  seenByDoctorAt: string | null;
  disposedAt: string | null;
  disposition: string | null;
  dispositionNotes: string | null;
  referredTo: string | null;
  patient: (ErPatientSummary & { phone: string | null; isDeceased: boolean }) | null;
  attendingDoctor: ErDoctor | null;
  waitingMinutes: number;
  vitals: ErVitalRow[];
  orders: ErOrderRow[];
  admission: { id: string; number: string; status: string } | null;
  death: { id: string; causeOfDeath: string; deceasedAt: string; patientId: string | null } | null;
  registrationInvoice: { id: string; number: string; status: string } | null;
}

type VitalsReading = Pick<ErVitals, 'bpSystolic' | 'bpDiastolic' | 'pulse' | 'spo2'>;

export function vitalsSummary(reading: VitalsReading): string[] {
  const parts: string[] = [];
  if (reading.bpSystolic !== null && reading.bpDiastolic !== null) {
    parts.push(`BP ${reading.bpSystolic}/${reading.bpDiastolic}`);
  }
  if (reading.pulse !== null) parts.push(`P ${reading.pulse}`);
  if (reading.spo2 !== null) parts.push(`SpO2 ${reading.spo2}%`);
  return parts;
}

export function canSaveMlc(input: { isMlc: boolean; mlcDetails: string; reason: string }): boolean {
  const reasonLength = input.reason.trim().length;
  if (reasonLength < MIN_MLC_REASON || reasonLength > MAX_MLC_REASON) return false;
  return !input.isMlc || input.mlcDetails.trim().length > 0;
}

export function canIdentify(input: { patientId: string; attachesDeath: boolean; confirmed: boolean; basis: string }): boolean {
  if (!input.patientId) return false;
  if (!input.attachesDeath) return true;
  return input.confirmed && input.basis.trim().length >= MIN_IDENTITY_BASIS;
}
